import { string as yupString, object as yupObject } from 'yup';

/**
 * Constants and patterns shared between Azure/Microsoft integration components
 */

// Constants for Microsoft Partner App Client IDs
export const MICROSOFT_PARTNER_APP_CLIENT_ID =
  '4990cffe-04e8-4e8b-808a-1175604b879f';
export const MICROSOFT_PARTNER_CENTER_APP_CLIENT_ID =
  'fa3d9a0c-3fb0-42cc-9193-47c7ecd2edbd';

// Common validation patterns
export const AZURE_IDS_PATTERN =
  /^[\w\d]{8}-[\w\d]{4}-[\w\d]{4}-[\w\d]{4}-[\w\d]{12}$/m;
export const AZURE_SECRET_PATTERN = /^\S+$/;

// Environment variables
export const MSFT_CLOUD_CONNECTORS_URL =
  process.env.REACT_APP_MSFT_CLOUD_CONNECTORS_URL;

// Common schema for credential forms
export interface CredentialValues {
  tenantId: string;
  applicationId: string;
  applicationSecretKeyValue: string;
  applicationSecretKeyExpirationDate: string;
}

export const credentialSchema = yupObject().shape<CredentialValues>({
  tenantId: yupString()
    .required('Tenant ID is required to continue')
    .matches(AZURE_IDS_PATTERN, {
      excludeEmptyString: true,
      message:
        'Tenant ID must be alphanumeric and match the pattern: 8char-4char-4char-4char-12char',
    }),
  applicationId: yupString()
    .required('Application ID is required to continue')
    .matches(AZURE_IDS_PATTERN, {
      excludeEmptyString: true,
      message:
        'Application ID must be alphanumeric and match the pattern: 8char-4char-4char-4char-12char',
    }),
  applicationSecretKeyValue: yupString()
    .required('Application Secret Key Value is required to continue')
    .matches(AZURE_SECRET_PATTERN, {
      excludeEmptyString: true,
      message: 'Application Secret Key Value must not include whitespace.',
    }),
  applicationSecretKeyExpirationDate: yupString().required(
    'Application Secret Key Expiration Date is required to continue',
  ),
});

/**
 * Sends credential values to the cloud connector
 */
export const sendCredentialsToCloudConnector = async (
  values: CredentialValues,
  token: string,
): Promise<any> => {
  const response = await fetch(`${MSFT_CLOUD_CONNECTORS_URL}/api/settings`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      tenantID: values.tenantId,
      applicationClientID: values.applicationId,
      clientSecret: values.applicationSecretKeyValue,
      clientSecretExpires: new Date(
        values.applicationSecretKeyExpirationDate,
      ).toISOString(),
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Request failed with status ${response.status}: ${errorText}`,
    );
  }

  return await response.json();
};

/**
 * Transforms user data to form values
 */
export const userToCredentialValues = (user?: any): CredentialValues => {
  return {
    tenantId: '',
    applicationId: '',
    applicationSecretKeyValue: '',
    applicationSecretKeyExpirationDate:
      user?.vendor?.configuration?.azure?.exp_date || '',
  };
};

/**
 * Transforms form values to vendor configuration update
 */
export const valuesToVendorConfigurationMsftUpdate = (
  values: CredentialValues,
) => {
  return {
    app_id: values.applicationId,
    app_secret: values.applicationSecretKeyValue,
    exp_date: values.applicationSecretKeyExpirationDate,
    tenant_id: values.tenantId,
  };
};

// Keep the original function name for backward compatibility
export const valuesToVendorConfigurationAzureUpdate =
  valuesToVendorConfigurationMsftUpdate;

/**
 * Updates payout and tax profiles in the commercial marketplace array
 * If one profile exists but not the other, both are added.
 * If both exist, both are removed.
 * If neither exists, both are added.
 */
export const updatePayoutAndTaxProfiles = (
  commercial_marketplace: string[],
): string[] => {
  // This should never happen, but protect against unlikely cases where one key is included but not the other, by adding both keys
  const includesTaxProfile = commercial_marketplace.includes('tax-profile');
  const includesPayoutProfile =
    commercial_marketplace.includes('payout-profile');

  if (includesTaxProfile && !includesPayoutProfile) {
    return commercial_marketplace.concat('payout-profile');
  }

  if (includesPayoutProfile && !includesTaxProfile) {
    return commercial_marketplace.concat('tax-profile');
  }

  // If both exist, remove both
  if (includesTaxProfile && includesPayoutProfile) {
    return commercial_marketplace.filter(
      (item) => item !== 'tax-profile' && item !== 'payout-profile',
    );
  }

  // If neither exists, add both
  return commercial_marketplace.concat(['tax-profile', 'payout-profile']);
};
